import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { ROUTES } from '../../app/config';
import { FullScreenLoader } from '../../components/layout/FullScreenLoader';
import { ErrorState } from '../../components/ui';
import { realAuthError } from '../../auth/errors';
import { consumeReturnTo } from '../../auth/oidc';

/**
 * Retorno desde Keycloak.
 *
 * `react-oidc-context` ya ha canjeado el codigo por tokens cuando llegamos
 * aqui; esta pantalla solo decide a donde continuar. La URL ya viene limpia
 * de `code`/`state` gracias a `onSigninCallback`, asi que un F5 no reintenta
 * canjear un codigo consumido (evita el bucle clasico de redirecciones).
 */
export function CallbackPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const fatalError = realAuthError(auth.error);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) return;

    if (auth.isAuthenticated) {
      const returnTo = consumeReturnTo();
      navigate(returnTo ?? ROUTES.home, { replace: true });
      return;
    }

    if (!fatalError) {
      navigate(ROUTES.login, { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, fatalError, navigate]);

  if (fatalError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          title="No se pudo completar el inicio de sesion"
          description={fatalError.message}
          onRetry={() => navigate(ROUTES.login, { replace: true })}
        />
      </div>
    );
  }

  return <FullScreenLoader message="Completando el inicio de sesion..." />;
}
